import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { cn } from '../utils/cn';
import { useSeo } from '../utils/useSeo';

type Plan = {
  badge?: string;
  billing: string;
  cta: string;
  description: string;
  featured?: boolean;
  id: 'monthly' | 'yearly';
  period: string;
  points: string[];
  price: string;
  savings?: string;
};

const plans: Plan[] = [
  {
    billing: 'Monthly',
    cta: 'Start Monthly',
    description: 'Perfect if you want to stay flexible while scaling your deals.',
    id: 'monthly',
    period: '/month',
    points: ['Full access to Deals Finder', 'AI Photography tools included', 'Garage and Finance included'],
    price: '$60',
  },
  {
    badge: 'Best Value',
    billing: 'Yearly',
    cta: 'Start Yearly',
    description: 'Lock in the lower annual price and save while growing faster.',
    featured: true,
    id: 'yearly',
    period: '/year',
    points: ['Everything in monthly plan', 'Priority support', 'Save $220 vs monthly billing'],
    price: '$500',
    savings: 'Save $220/year',
  },
];

const ctaClass =
  'relative isolate h-14 w-full overflow-hidden rounded-[10px] border border-black/65 bg-[#08090c] px-7 text-lg font-bold tracking-[0.04em] text-white shadow-none hover:border-white/35 hover:bg-[#141820] focus-visible:outline-[#08090c] after:pointer-events-none after:absolute after:inset-y-0 after:-left-[55%] after:w-[42%] after:skew-x-[-22deg] after:bg-gradient-to-r after:from-transparent after:via-white/55 after:to-transparent after:opacity-0 after:transition-all after:duration-700 hover:after:left-[130%] hover:after:opacity-100';

export function GetPage() {
  useSeo({
    description:
      'Choose a Profit Cars plan: $60 monthly or $500 yearly. Get deals finder, AI photography, and finance tracking in one system.',
    image: '/car-dealer-hero.png',
    keywords: [
      'profit cars pricing',
      'car dealer software pricing',
      'deals finder plan',
      'ai photography pricing',
      'garage finance software',
    ],
    path: '/get',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      description: 'Profit Cars software plans for car dealers and flippers.',
      name: 'Profit Cars',
      offers: [
        {
          '@type': 'Offer',
          name: 'Monthly Plan',
          price: '60',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          name: 'Yearly Plan',
          price: '500',
          priceCurrency: 'USD',
        },
      ],
    },
    title: 'Get Profit Cars | Pricing',
    type: 'website',
  });

  return (
    <main className="min-h-screen bg-[#ececec] text-[#111827]">
      <section className="border-b border-black/10 bg-[#e6e7ea]">
        <div className="mx-auto w-full max-w-6xl px-6 pb-12 pt-12 lg:px-8">
          <Link
            className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white/70 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-black/30 hover:text-black"
            to="/"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>

          <div className="mt-10 max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#2f5b1b]">Profit Cars Access</p>
            <h1 className="mt-4 font-display text-6xl leading-[0.92] text-black sm:text-7xl">Choose your plan.</h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-700">
              One platform for sourcing deals, improving listing visuals, and running your garage and finances in one
              workflow.
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2">
            {plans.map((plan) => (
              <article
                className={cn(
                  'rounded-[28px] border border-black/10 bg-white p-6 sm:p-7',
                  plan.featured && 'border-[#53b12c]/40 bg-[#f5fbf3]'
                )}
                key={plan.id}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">{plan.billing}</p>
                    <p className="mt-3 text-5xl font-extrabold text-black sm:text-6xl">
                      {plan.price}
                      <span className="ml-1 text-lg font-semibold text-slate-500">{plan.period}</span>
                    </p>
                    <p className="mt-4 max-w-md text-base leading-relaxed text-slate-700">{plan.description}</p>
                  </div>

                  {plan.badge && (
                    <span className="inline-flex rounded-full border border-[#53b12c]/45 bg-[#53b12c]/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#2f5b1b]">
                      {plan.badge}
                    </span>
                  )}
                </div>

                {plan.savings && (
                  <p className="mt-5 inline-flex rounded-full border border-[#53b12c]/45 bg-[#53b12c]/10 px-3 py-1 text-sm font-semibold text-[#2f5b1b]">
                    {plan.savings}
                  </p>
                )}

                <ul className="mt-6 space-y-3 text-sm leading-relaxed text-slate-700 sm:text-base">
                  {plan.points.map((point) => (
                    <li className="flex items-start gap-3" key={point}>
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#53b12c]" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  <Button className={ctaClass}>
                    {plan.cta}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
